import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";

import { STEP_COLLAPSE } from "../steps";

import { CollapsingBlock } from "./CollapsingBlock";

// The shared collapse feel used by the wizard's self-collapsing field steps.
const collapseTransition = {
    duration: STEP_COLLAPSE.moveDuration,
    ease: STEP_COLLAPSE.ease
} as const;

/**
 * The shared shape of a step that collapses to a *persistent field*: a heading
 * (+ optional description) that folds its height away on collapse, leaving the
 * field — `children` — in place to rise into the stacked summaries above it.
 *
 * `VerifyWalletGroup` and `RewardsGroup` are thin configs of this. Mechanism
 * only: the caller supplies the copy and the field (plus any below-field
 * extras like a validation message or an action button, which simply ride
 * along as additional children).
 */
export function StepFieldGroup({
    collapsed,
    heading,
    description,
    children
}: {
    collapsed: boolean;
    /** Optional heading. Omit it (e.g. when a persistent heading sits above the
     *  whole stack) and the heading block is skipped entirely. */
    heading?: React.ReactNode;
    /** Optional supporting copy under the heading (muted). */
    description?: React.ReactNode;
    /** The persistent field (+ any below-field extras) — stays mounted across
     *  collapse as the resting summary. */
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <div className="flex flex-col">
            {/* Heading + description belong to the expanded state only. On
                collapse they fold their own height away so the field below
                rises smoothly into place. The bottom spacing (`pb-4`) lives
                inside the collapsing box so the gap collapses with it. */}
            {(heading || description) && (
                <CollapsingBlock
                    show={!collapsed}
                    transition={collapseTransition}
                >
                    <div className="flex flex-col gap-1.5 pb-4">
                        {heading && (
                            <Text
                                variant="h3"
                                render={<h3 />}
                                className="text-foreground-base"
                            >
                                {heading}
                            </Text>
                        )}
                        {description && (
                            <Text
                                variant="small"
                                className="text-foreground-muted"
                            >
                                {description}
                            </Text>
                        )}
                    </div>
                </CollapsingBlock>
            )}
            {children}
        </div>
    );
}
